// apps/client/src/pages/settings/TwoFactorSecurityCard.tsx
//
// Settings -> Security: the two-factor authentication card.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS IS ITS OWN COMPONENT, AND WHY IT NO LONGER READS `claims.mfaEnabled`
// ─────────────────────────────────────────────────────────────────────────────
// This card used to live inline in SettingsPage and branch on the legacy
// `claims?.mfaEnabled` custom claim. That claim is written ONLY by the retired
// bespoke TOTP flow (`client-functions/src/security/verifyAndEnableMfa.ts`);
// native GCIP enrolment sets a second factor and never sets it. So the card told
// a natively-enrolled user that 2FA was off and offered to "Enable" it, which is
// the same wrong signal that produced the enrolment loop MfaEnforcementGuard was
// moved off (see `hasEnrolledSecondFactor` in lib/nativeMfa.ts).
//
// The state source is now `hasEnrolledSecondFactor(auth.currentUser)`: a live
// SDK read of `multiFactor(user).enrolledFactors`, the SAME assertion the
// sign-in blocking function (`beforeSignInMfaGate`) makes server-side, so what
// this card says and what the sign-in path enforces cannot disagree. Read on
// every render rather than cached in state, so a user returning from
// /enroll-2fa sees the enrolled state without a token refresh.
//
// Extracted from SettingsPage (785 lines, useOutletContext + Firestore + auth)
// so this behaviour is directly renderable in a test. The card owns no state and
// performs no writes.
//
// NO SELF-SERVE UNENROL. GCIP would allow `multiFactor(user).unenroll(...)`, but
// removal stays an administrator action: a user who can silently drop their own
// second factor turns an enforced control into an optional one, and the reset
// path is already admin-mediated (MfaRecoveryPanel). The card says so in one
// quiet line instead of offering a button that must not exist.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Smartphone } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { getEnrolledSecondFactor, hasEnrolledSecondFactor } from '../../lib/nativeMfa';

/** The enrolment route this card sends unenrolled users to (AppRoutes.tsx). */
const ENROLL_PATH = '/enroll-2fa';

/**
 * `enrollmentTime` arrives as a UTC date string. Rendered in Australian format
 * for an Australian product; an unparseable or absent value simply drops the
 * date line rather than printing "Invalid Date" at the user.
 */
function formatEnrolmentDate(enrollmentTime: string | null | undefined): string | null {
  if (!enrollmentTime) return null;
  const parsed = new Date(enrollmentTime);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const TwoFactorSecurityCard: React.FC = () => {
  const navigate = useNavigate();

  // Live SDK read, every render. Never the legacy `mfaEnabled` claim.
  const currentUser = auth.currentUser;
  const enrolled = !!currentUser && hasEnrolledSecondFactor(currentUser);
  const factor = enrolled && currentUser ? getEnrolledSecondFactor(currentUser) : null;
  const enrolledOn = formatEnrolmentDate(factor?.enrollmentTime);

  return (
    <div className="mt-8 p-6 bg-gray-700/30 rounded-lg border border-gray-600">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h4 className="text-lg font-semibold text-white">Two-Factor Authentication</h4>
        {enrolled && (
          <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/40">
            <ShieldCheck size={14} />
            Enabled
          </span>
        )}
      </div>

      {enrolled ? (
        <>
          <p className="text-sm text-gray-400 leading-relaxed">
            Two-factor authentication protects your sign-in. Codes come from your
            authenticator app.
          </p>

          <div className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-md bg-gray-800/60 border border-gray-700">
            <Smartphone size={18} className="flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {factor?.displayName || 'Authenticator app'}
              </div>
              {enrolledOn && (
                <div className="text-xs text-gray-400 mt-0.5">Enrolled {enrolledOn}</div>
              )}
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Removing two-factor authentication is an administrator action. Contact your
            administrator if you need it reset.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-400 leading-relaxed">
            Add a second sign-in step with an authenticator app (Google Authenticator, Zoho
            OneAuth, 1Password, Authy).
          </p>

          <button
            onClick={() => navigate(ENROLL_PATH)}
            className="mt-4 px-6 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 transform hover:scale-[1.02]"
          >
            Set up two-factor authentication
          </button>
        </>
      )}
    </div>
  );
};

export default TwoFactorSecurityCard;
